import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Button,
  makeStyles,
  FormControlLabel,
  Typography,
} from "@material-ui/core";
import { Select } from "@ofqual/portal-components/components/Select";
import React, { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { ActionDialogProps } from "./DialogProps";
import { Error } from "../../../common/Error";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { statementOfAssuranceService } from "../../../../services";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import {
  AdditionalEvidenceRequest,
  AdditionalEvidenceRequestStatus,
  toApiModel,
} from "../../../../domain/AdditionalEvidenceRequest";
import { isFutureDate } from "@ofqual/portal-core/core/utils/dateUtils";
import { Checkbox } from "@ofqual/portal-components/components/Checkbox";
import { sendRequestEvidence } from "../../../../redux/actions";

const useStyles = makeStyles((theme) => ({
  changeDetails: {
    padding: 15,
  },
  actions: {
    padding: 15,
  },
  disabledLink: {
    color: "inherit",
    textDecoration: "none",
  },
}));

const defaultValues: Partial<AdditionalEvidenceRequest> = {
  requestTitle: null,
  dueDate: null,
  introductionText: null,
  statementsOfAssuranceStrs: [],
  isOtherEvidenceRequired: false,
  otherEvidenceText: null,
  allowAttachments: false,
};

type AdditionalEvidenceDialogProps = ActionDialogProps & {
  existingEvidenceRequests: AdditionalEvidenceRequest[];
};

export const AdditionalEvidenceDialog: React.FC<AdditionalEvidenceDialogProps> = ({
  application,
  onClose,
  existingEvidenceRequests,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [isSaveInProgress, setIsSaveInProgress] = useState<boolean>(false);

  const activeRequests = existingEvidenceRequests.find(
    (x) =>
      x.status === AdditionalEvidenceRequestStatus.New ||
      x.status === AdditionalEvidenceRequestStatus.InProgress
  );
  const canCreateRequest = !activeRequests;

  const {
    handleSubmit,
    control,
    errors,
    setValue,
  } = useForm<AdditionalEvidenceRequest>({
    defaultValues,
  });

  const { isOtherEvidenceRequired } = useWatch<AdditionalEvidenceRequest>({
    control,
    name: ["isOtherEvidenceRequired"],
  });

  const [statementOfAssuranceLookups] = useLookup({
    service: statementOfAssuranceService,
    id: "id",
    label: "name",
  });

  const onSubmit = (newEvent: AdditionalEvidenceRequest) => {
    setIsSaveInProgress(true);
    const mappedEvent = toApiModel(newEvent, statementOfAssuranceLookups);
    dispatch(
      sendRequestEvidence(
        application.organisationID.toString(),
        application.id,
        mappedEvent
      )
    );
    onClose();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="additional-evidence-dialog"
    >
      <DialogTitle data-test="additional-evidence-title">
        Request Additional Evidence
        <Divider />
      </DialogTitle>

      <DialogContent>
        {canCreateRequest ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={classes.changeDetails}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="requestTitle"
                    rules={{ required: true }}
                    as={
                      <TextField
                        data-test="request-title"
                        multiline
                        label="Request Title"
                        required
                      />
                    }
                  />
                  {errors.requestTitle && <Error>This field is required</Error>}
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    rules={{
                      required: true,
                      validate: (value) => (isFutureDate(value) ? true : false),
                    }}
                    name="dueDate"
                    as={
                      <DatePicker
                        dataTest="due-date"
                        label="Due Date"
                        required
                      />
                    }
                  />
                  {errors.dueDate?.type === "required" && (
                    <Error>This field is required</Error>
                  )}
                  {errors.dueDate?.type === "validate" && (
                    <Error>Date must be in the future</Error>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="introductionText"
                    rules={{ required: true }}
                    as={
                      <TextField
                        data-test="introductory-text"
                        multiline
                        rows={5}
                        label="Introductory Text"
                        required
                      />
                    }
                  />
                  {errors.introductionText && (
                    <Error>This field is required</Error>
                  )}
                </Grid>
                <Grid item md={12}>
                  <Controller
                    control={control}
                    name="statementsOfAssuranceStrs"
                    as={
                      <Select
                        label="Statements of Assurance"
                        datatest="statements-of-assurance"
                        selectVariant="multiple"
                      >
                        {statementOfAssuranceLookups}
                      </Select>
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="isOtherEvidenceRequired"
                    render={(props) => {
                      const _onChange = (event: any) => {
                        // reset below fields
                        setValue("otherEvidenceText", null);
                        setValue("allowAttachments", null);
                        props.onChange(event.target.checked);
                      };
                      return (
                        <FormControlLabel
                          control={
                            <Checkbox
                              onChange={_onChange}
                              checked={props.value}
                            />
                          }
                          label="Other Evidence Required?"
                        />
                      );
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="otherEvidenceText"
                    as={
                      <TextField
                        data-test="other-evidence-text"
                        multiline
                        rows={5}
                        label="Other Evidence Text"
                        disabled={!isOtherEvidenceRequired}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="allowAttachments"
                    render={(props) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(e) => {
                              props.onChange(e.target.checked);
                            }}
                            checked={props.value}
                            disabled={!isOtherEvidenceRequired}
                          />
                        }
                        label="Allow Attachments?"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </div>
          </form>
        ) : (
          <Typography variant="body1">
            A current evidence request is already active; only one request can
            be active at a time.
          </Typography>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          data-test="cancel-additional-evidence-button"
          color="primary"
        >
          Close
        </Button>

        {canCreateRequest ? (
          <Button
            onClick={handleSubmit(onSubmit)}
            data-test="add-additional-evidence-button"
            className={isSaveInProgress ? classes.disabledLink : undefined}
            disabled={isSaveInProgress}
            color="primary"
          >
            Confirm
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};
